import React, { useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  formatCurrency,
  formatNumber,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";

import OrbLoader from "../common/OrbLoader";
export type LineItemMetricType =
  | "impressions"
  | "clicks"
  | "cost"
  | "realCost"
  | "ctr"
  | "all";

interface LineItemMetricsChartProps {
  dailyData: components["schemas"]["DailyLineItemMetrics"][] | undefined;
  isLoading: boolean;
  title?: string;
  currency?: components["schemas"]["currency"];
  isAdmin?: boolean;
}

const LineItemMetricsChart: React.FC<LineItemMetricsChartProps> = ({
  dailyData,
  isLoading,
  title = "Metrics Timeline",
  currency = "EUR",
  isAdmin = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedMetrics, setSelectedMetrics] = useState<LineItemMetricType[]>([
    "all",
  ]);

  // Define metric labels
  const metricLabels: Record<LineItemMetricType, string> = {
    all: t("common.all", { defaultValue: "All" }),
    impressions: t("home.impressions", { defaultValue: "Impressions" }),
    clicks: t("home.clicks", { defaultValue: "Clicks" }),
    cost: t("home.dashboardInvestment", { defaultValue: "Investment" }),
    realCost: t("campaigns.realCost", { defaultValue: "Real Cost" }),
    ctr: t("home.ctr", { defaultValue: "CTR" }),
  };

  // Define colors for each metric to match MetricsTimelineChart
  const metricColors: Record<Exclude<LineItemMetricType, "all">, string> = {
    ctr: theme.palette.primary.main,
    impressions: "#9c27b0", // Purple
    clicks: "#2196f3", // Blue
    cost: "#ff9800", // Orange
    realCost: theme.palette.secondary.main,
  };

  // Handle metric selection change
  const handleMetricChange = (
    event: React.MouseEvent<HTMLElement>,
    metric: LineItemMetricType
  ) => {
    event.preventDefault();

    // Special handling for 'all' option
    if (metric === "all") {
      // If 'all' is already selected, do nothing
      if (selectedMetrics.includes("all")) {
        return;
      }
      // Otherwise, select only 'all'
      setSelectedMetrics(["all"]);
      return;
    }

    // If selecting a specific metric, remove 'all' if present
    let newSelectedMetrics = selectedMetrics.filter((m) => m !== "all");

    // Toggle the selected metric
    if (selectedMetrics.includes(metric)) {
      newSelectedMetrics = newSelectedMetrics.filter((m) => m !== metric);

      // If no metrics are selected, default to 'all'
      if (newSelectedMetrics.length === 0) {
        setSelectedMetrics(["all"]);
        return;
      }
    } else {
      newSelectedMetrics.push(metric);
    }

    setSelectedMetrics(newSelectedMetrics);
  };

  // Format the data for the chart
  const chartData = useMemo(() => {
    if (!dailyData || dailyData.length === 0) return [];

    return dailyData.map((day) => ({
      date: new Date(day.date).toLocaleDateString(),
      impressions: day.impressions,
      clicks: day.clicks,
      cost: day.cost,
      realCost: day.realCost,
      ctr: day.ctr,
      cpm: day.cpm,
      cpc: day.cpc,
    }));
  }, [dailyData]);

  // Define types for the tooltip props
  interface TooltipProps {
    active?: boolean;
    payload?: {
      name: string;
      value: number;
      color: string;
    }[];
    label?: string;
  }

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: theme.shadows[2],
          }}
        >
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map(
            (
              entry: {
                name: string;
                dataKey?: string;
                value: number;
                color: string;
              },
              index: number
            ) => (
              <Typography
                key={`item-${index}`}
                variant="body2"
                sx={{ color: entry.color }}
              >
                {/* Por dataKey: el nombre es la etiqueta traducida y en español no casaba */}
                {entry.name}:{" "}
                {entry.dataKey === "impressions" || entry.dataKey === "clicks"
                  ? formatNumber(entry.value)
                  : entry.dataKey === "ctr"
                    ? formatPercentageNoMultiply(entry.value)
                    : formatCurrency(entry.value, currency)}
              </Typography>
            )
          )}
        </Box>
      );
    }
    return null;
  };

  // Show loading state if data is loading
  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 450,
          mt: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <OrbLoader size="large" />
      </Box>
    );
  }

  // Show no data message if there is no data
  if (!chartData || chartData.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 450,
          mt: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {t("common.noData", { defaultValue: "No data available" })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: 450, mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: { xs: "wrap", md: "nowrap" },
          gap: { xs: 1, md: 0 },
        }}
      >
        <Typography
          variant="h6"
          fontWeight={500}
          color="text.primary"
          sx={{
            maxWidth: { xs: "100%", sm: "50%" },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mr: 1,
          }}
          title={title} // Show full title on hover
        >
          {title}
        </Typography>

        {/* Metric selection */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {Object.entries(metricLabels).map(([key, label]) => {
            const metricKey = key as LineItemMetricType;
            const isSelected = selectedMetrics.includes(metricKey);

            // Hide Real Cost for non-admin users
            if (metricKey === "realCost" && !isAdmin) {
              return null;
            }

            return (
              <Box
                key={metricKey}
                component="button"
                onClick={(e) => handleMetricChange(e, metricKey)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "16px",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: isSelected
                    ? metricKey === "all"
                      ? theme.palette.text.primary
                      : metricColors[
                          metricKey as Exclude<LineItemMetricType, "all">
                        ]
                    : "transparent",
                  color: isSelected ? "white" : "inherit",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  fontFamily: "inherit",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? theme.palette.action.selected
                      : theme.palette.action.hover,
                  },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      </Box>
      <ResponsiveContainer width="100%" height="90%">
        <LineChart
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="date"
            tick={{ fill: theme.palette.text.secondary }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={{ stroke: theme.palette.divider }}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: theme.palette.text.secondary }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={{ stroke: theme.palette.divider }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fill: theme.palette.text.secondary }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={{ stroke: theme.palette.divider }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{
              paddingTop: 10,
              paddingBottom: 10,
              width: "95%",
              margin: "0 auto",
              fontSize: "0.75rem",
            }}
            align="center"
            verticalAlign="bottom"
            iconSize={10}
            iconType="circle"
            layout="horizontal"
          />
          {/* Only render lines for selected metrics */}
          {(selectedMetrics.includes("impressions") ||
            selectedMetrics.includes("all")) && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="impressions"
              name={metricLabels.impressions}
              stroke={metricColors.impressions}
              strokeWidth={2}
              dot={{ r: 3, fill: metricColors.impressions }}
              activeDot={{ r: 5, fill: metricColors.impressions }}
            />
          )}
          {(selectedMetrics.includes("clicks") ||
            selectedMetrics.includes("all")) && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="clicks"
              name={metricLabels.clicks}
              stroke={metricColors.clicks}
              strokeWidth={2}
              dot={{ r: 3, fill: metricColors.clicks }}
              activeDot={{ r: 5, fill: metricColors.clicks }}
            />
          )}
          {(selectedMetrics.includes("cost") ||
            selectedMetrics.includes("all")) && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="cost"
              name={metricLabels.cost}
              stroke={metricColors.cost}
              strokeWidth={2}
              dot={{ r: 3, fill: metricColors.cost }}
              activeDot={{ r: 5, fill: metricColors.cost }}
            />
          )}
          {(selectedMetrics.includes("realCost") ||
            selectedMetrics.includes("all")) &&
            chartData.some((day) => day.realCost !== undefined) && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="realCost"
                name={metricLabels.realCost}
                stroke={metricColors.realCost}
                strokeWidth={2}
                dot={{ r: 3, fill: metricColors.realCost }}
                activeDot={{ r: 5, fill: metricColors.realCost }}
              />
            )}
          {(selectedMetrics.includes("ctr") ||
            selectedMetrics.includes("all")) && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="ctr"
              name={metricLabels.ctr}
              stroke={metricColors.ctr}
              strokeWidth={2}
              dot={{ r: 3, fill: metricColors.ctr }}
              activeDot={{ r: 5, fill: metricColors.ctr }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default LineItemMetricsChart;
